import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { DocumentUploader } from "./DocumentUploader";
import { AttachmentGallery } from "./AttachmentGallery";
import { useDropZone } from "../lib/useDropZone";
import { Paperclip, Plus, Trash2, X, Upload } from "lucide-react";

const MAX_ALLEGATI = 5;

/**
 * Card riusabile per la sezione "Allegati della gestione" di una manutenzione
 * in stato Affidata / In lavorazione. Visibile a Studio e al fornitore incaricato.
 * Max 5 allegati totali. Ogni operazione (add/remove) aggiorna la cronologia.
 *
 * Iter122k59 · Drag & drop direttamente sulla card: rilasciando file sulla card
 * si esegue upload + commit automatico verso `/tickets/{id}/allegati-gestione`.
 */
export function AllegatiGestioneCard({ ticket, user, onUpdate, className = "" }) {
  const [open, setOpen] = useState(false);
  const [nuovi, setNuovi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);

  const list = ticket.allegati_gestione || [];
  const rimasti = Math.max(0, MAX_ALLEGATI - list.length);
  const disabled = rimasti === 0;

  // Iter122k59 · Drag & drop sulla card: upload + POST immediato (senza aprire il form)
  const handleDrop = async (files) => {
    if (disabled) {
      toast.error(`Limite raggiunto: max ${MAX_ALLEGATI} allegati`);
      return;
    }
    const arr = Array.from(files).slice(0, rimasti);
    if (arr.length === 0) return;
    setDropBusy(true);
    try {
      const ids = [];
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/documento", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ids.push(data.id);
      }
      const { data } = await api.post(`/tickets/${ticket.id}/allegati-gestione`, {
        allegati_ids: ids,
      });
      onUpdate?.(data);
      toast.success(ids.length === 1 ? "File aggiunto" : `${ids.length} file aggiunti`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito");
    } finally { setDropBusy(false); }
  };
  const { dragProps, isDragOver } = useDropZone(handleDrop, { disabled: disabled || dropBusy });

  const salva = async () => {
    if (nuovi.length === 0) { toast.error("Seleziona almeno un file"); return; }
    if (nuovi.length > rimasti) { toast.error(`Puoi aggiungere ancora ${rimasti} file al massimo`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/allegati-gestione`, {
        allegati_ids: nuovi.map((n) => n.id),
      });
      onUpdate?.(data);
      toast.success(nuovi.length === 1 ? "File aggiunto" : `${nuovi.length} file aggiunti`);
      setNuovi([]);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aggiunta non riuscita");
    } finally { setBusy(false); }
  };

  const rimuovi = async (fid, nome) => {
    if (!window.confirm(`Rimuovere "${nome}" dagli allegati della gestione?`)) return;
    try {
      const { data } = await api.delete(`/tickets/${ticket.id}/allegati-gestione/${fid}`);
      onUpdate?.(data);
      toast.success("File rimosso");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rimozione non riuscita");
    }
  };

  const puoRimuovere = (a) => {
    if (user.role === "admin") return true;
    return a.caricato_ruolo === "Fornitore";
  };

  return (
    <Card {...dragProps}
      className={`p-4 border-2 border-slate-200 relative transition-all ${isDragOver ? "ring-2 ring-brand bg-brand/5" : ""} ${className}`}
      data-testid="allegati-gestione-card">
      {/* Iter122k59 · overlay drag-over per feedback visivo */}
      {isDragOver && !disabled && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
          <div className="flex items-center gap-2 text-brand font-bold text-base bg-white/95 px-4 py-2 rounded-full border-2 border-brand shadow-md">
            <Upload className="w-5 h-5" /> Rilascia qui per allegare
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="font-display text-lg font-bold flex items-center gap-2">
          <Paperclip className="w-5 h-5" /> Allegati della gestione
          <span className="text-sm font-semibold text-slate-500">({list.length}/{MAX_ALLEGATI})</span>
          {dropBusy && <span className="text-xs text-brand font-semibold animate-pulse">· Caricamento…</span>}
        </h4>
        {!open && (
          <Button data-testid="allegati-gestione-add-btn" size="sm"
            disabled={disabled || dropBusy} onClick={() => setOpen(true)}
            className="h-9 bg-brand hover:bg-brand-dark">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi file/foto
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Puoi caricare file/foto (max {MAX_ALLEGATI}) per documentare la gestione — <b>trascina qui i file</b> oppure usa il bottone.
        Ogni aggiunta o rimozione viene registrata nella cronologia.
      </p>

      {list.length > 0 ? (
        <div className="space-y-3">
          <AttachmentGallery docs={list} testidPrefix="allegato-gestione" />
          <div className="space-y-1">
            {list.map((a) => (
              <div key={a.id} data-testid={`allegato-gestione-meta-${a.id}`}
                className="flex items-center justify-between gap-2 text-xs text-slate-500 border-t border-slate-100 pt-1">
                <span><b className="text-slate-700">{a.original_filename}</b> — caricato da {a.caricato_da} ({a.caricato_ruolo}) il {new Date(a.caricato_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                {puoRimuovere(a) && (
                  <button type="button" data-testid={`allegato-gestione-del-${a.id}`}
                    onClick={() => rimuovi(a.id, a.original_filename)}
                    className="text-red-600 hover:text-red-800 shrink-0" title="Rimuovi allegato">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">Nessun allegato caricato.</p>
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-3" data-testid="allegati-gestione-form">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-slate-700">Nuovi allegati (rimanenti: {rimasti})</span>
            <button type="button" onClick={() => { setOpen(false); setNuovi([]); }}
              className="text-slate-400 hover:text-slate-700" title="Annulla">
              <X className="w-4 h-4" />
            </button>
          </div>
          <DocumentUploader docs={nuovi} onChange={setNuovi} testid="allegati-gestione-uploader" />
          <div className="actions-row">
            <Button data-testid="allegati-gestione-save-btn" disabled={busy || nuovi.length === 0}
              onClick={salva} className="h-10 bg-brand hover:bg-brand-dark">
              {busy ? "Caricamento..." : `Salva (${nuovi.length})`}
            </Button>
            <Button variant="outline" onClick={() => { setOpen(false); setNuovi([]); }} className="h-10">
              Annulla
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
